import React from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../StyledTypography';
import AlertTriangle from 'apollo-react-icons/AlertTriangle';
import ThumbsUpFilled from 'apollo-react-icons/ThumbsUpFilled';
import FileDocuments from 'apollo-react-icons/FileDocuments';
import ChartLine from 'apollo-react-icons/ChartLine';
import styles from './SubformNavigation.module.css';

/**
 * SubformNavigation - Reusable side navigation component with validation and status indicators
 *
 * DRS-style subform navigation with:
 * - Blue background for active item (no star icon)
 * - Warning icon for items with validation errors
 * - Thumbs up icon for approved subforms (highest priority)
 * - File documents icon for subforms with comments
 *
 * @param {Array} items - Array of items with { id, name } or custom shape
 * @param {number} activeIndex - Currently active item index
 * @param {Function} onNavigate - Callback when user clicks an item (receives index)
 * @param {React.ReactNode} children - Content to display for active item
 * @param {string} title - Navigation panel title (default: "Subforms")
 * @param {Function} getItemLabel - Custom function to get item label (default: item.name)
 * @param {Function} getItemKey - Custom function to get item key (default: item.id)
 * @param {Object} validationErrors - Validation state per subform { [subformId]: { hasError, errorCount } }
 * @param {boolean} showValidationIndicators - Whether to show validation indicators (default: true)
 * @param {Object} approvalStatus - Approval status per subform { [subformId]: { approved: boolean } }
 * @param {Object} commentStatus - Comment status per subform { [subformId]: boolean }
 * @param {React.ReactNode} headerSlot - Optional content to render above children (e.g., badges)
 * @param {Array} waveSubformIds - Array of subform IDs that are part of the wave
 */
const SubformNavigation = ({
  items = [],
  activeIndex = 0,
  onNavigate,
  children,
  title,
  getItemLabel,
  getItemKey,
  validationErrors = {},
  showValidationIndicators = true,
  approvalStatus = {},
  commentStatus = {},
  headerSlot,
  waveSubformIds = [],
}) => {
  const { t } = useTranslation();

  const defaultGetLabel = (item, index) =>
    item?.name || `${t('Subform')} ${index + 1}`;
  const defaultGetKey = (item, index) => item?.id || index;

  const labelFn = getItemLabel || defaultGetLabel;
  const keyFn = getItemKey || defaultGetKey;

  const getValidationState = (item) => {
    if (!showValidationIndicators) return null;
    const itemId = item?.id;
    if (!itemId) return null;
    return validationErrors[itemId] || null;
  };

  const getStatusIcon = (item) => {
    const itemId = item?.id;
    if (!itemId) return null;

    // Priority: Approval > Comments
    if (approvalStatus[itemId]?.approved) {
      return (
        <ThumbsUpFilled
          className={styles.approvalIcon}
          aria-label={t('subformNavigation.approved')}
        />
      );
    }

    if (commentStatus[itemId]) {
      return (
        <FileDocuments
          className={styles.commentIcon}
          aria-label={t('subformNavigation.hasComments')}
        />
      );
    }

    if (
      waveSubformIds &&
      waveSubformIds.length > 0 &&
      waveSubformIds.includes(itemId.toString())
    ) {
      return <ChartLine className={styles.waveIcon} />;
    }

    return null;
  };

  if (!items || items.length === 0) {
    return <>{children}</>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.navigationPanel}>
        <Typography variant="bodyDefault" className={styles.panelTitle}>
          {title || t('subformNavigation.title')}
        </Typography>
        <div className={styles.listContainer}>
          {items.map((item, index) => {
            const isActive = index === activeIndex;
            const validationState = getValidationState(item);
            const hasError = validationState?.hasError;
            const statusIcon = getStatusIcon(item);

            const itemClassName = [
              styles.tabItem,
              isActive && styles.tabItemActive,
              hasError && !isActive && styles.tabItemError,
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div
                key={keyFn(item, index)}
                className={itemClassName}
                onClick={() => onNavigate?.(index)}
              >
                <Typography className={styles.tabItemText}>
                  {labelFn(item, index)}
                </Typography>
                <div className={styles.iconContainer}>
                  {statusIcon}
                  {hasError && <AlertTriangle className={styles.errorIcon} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.contentPanel}>
        {headerSlot && <div className={styles.headerSlot}>{headerSlot}</div>}
        {children}
      </div>
    </div>
  );
};

export default SubformNavigation;
